import { useEffect, useState } from 'react'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import { usersApi } from '../../api/users'
import { chatsApi } from '../../api/chats'
import { Avatar } from './Avatar'
import type { User } from '../../types'
import { chatTitle, userName } from '../../lib/displayName'

interface Props {
  onClose: () => void
}

export function QuickSwitcher({ onClose }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [selectedIdx, setSelectedIdx] = useState(0)
  const { chats, setActiveChat, upsertChat } = useChatStore()
  const { addToast } = useUiStore()

  // Filter chats by query
  const filteredChats = query
    ? chats.filter((c) =>
        chatTitle(c).toLowerCase().includes(query.toLowerCase()),
      )
    : chats.slice(0, 6)

  // Search users when query >= 2 chars
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    const t = setTimeout(async () => {
      try {
        const res = await usersApi.search(query)
        setResults(res.users ?? [])
      } catch {
        setResults([])
      }
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  const totalItems = filteredChats.length + results.length

  const openChat = (chatId: string) => {
    setActiveChat(chatId)
    onClose()
  }

  const openUserChat = async (user: User) => {
    try {
      const chat = await chatsApi.getOrCreate(user.id)
      upsertChat({ ...chat, peer: user })
      setActiveChat(chat.id)
      onClose()
    } catch {
      addToast('error', 'Не удалось открыть чат')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((i) => Math.min(i + 1, totalItems - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIdx < filteredChats.length) {
        openChat(filteredChats[selectedIdx].id)
      } else {
        const user = results[selectedIdx - filteredChats.length]
        if (user) openUserChat(user)
      }
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-lg bg-bg-primary rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-divider">
          <svg
            className="text-txt-meta w-4 h-4 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIdx(0)
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            placeholder="Поиск чатов или пользователей..."
            className="flex-1 bg-transparent text-txt-primary outline-none text-[15px] placeholder-txt-meta"
          />
          <kbd className="text-txt-meta text-xs border border-divider rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {filteredChats.length > 0 && (
            <>
              {query && (
                <p className="px-4 py-1 text-txt-meta text-xs">Chats</p>
              )}
              {filteredChats.map((chat, i) => (
                <button
                  key={chat.id}
                  onClick={() => openChat(chat.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    selectedIdx === i ? 'bg-bg-hover' : 'hover:bg-bg-hover'
                  }`}
                >
                  <Avatar
                    name={chatTitle(chat)}
                    src={chat.avatar_url ?? chat.peer?.avatar_url}
                    size={36}
                  />
                  <span className="text-txt-primary text-sm">
                    {chatTitle(chat)}
                  </span>
                </button>
              ))}
            </>
          )}

          {results.length > 0 && (
            <>
              <p className="px-4 py-1 text-txt-meta text-xs">People</p>
              {results.map((user, i) => (
                <button
                  key={user.id}
                  onClick={() => openUserChat(user)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    selectedIdx === filteredChats.length + i ? 'bg-bg-hover' : 'hover:bg-bg-hover'
                  }`}
                >
                  <Avatar
                    name={userName(user)}
                    src={user.avatar_url}
                    size={36}
                    online={user.online}
                  />
                  <div>
                    <p className="text-txt-primary text-sm">
                      {userName(user)}
                    </p>
                    {user.username && (
                      <p className="text-txt-meta text-xs">@{user.username}</p>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}

          {!filteredChats.length && !results.length && query.length >= 2 && (
            <p className="text-txt-secondary text-sm text-center py-8">No results found</p>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-divider px-4 py-2 flex items-center gap-3 text-txt-meta text-xs">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>Esc — закрыть</span>
        </div>
      </div>
    </div>
  )
}
